import React, { useState, useEffect } from "react";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import IconButton from "@mui/material/IconButton";
import { ReactComponent as CloseIcon } from "../../assets/Icons/close.svg";
import { trackClickEvent } from "../../mixpanel";
import { useHomePage } from "../../contexts/HomePage";
import MarketingContent from "./MarketingContent";
import { useAuth } from "../../contexts/Auth";
import { useBasicInfo } from "../../contexts/BasicInfo";
import { useGeneralProfile } from "../../contexts/Organization";
import { treatment_program } from "../../constants/common";
import { isVideoFile } from "../../utils/helper";

const MarketingCard = () => {
  const { marketingData } = useHomePage();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(true);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [startTime, setStartTime] = useState(0);
  const [hasMoved, setHasMoved] = useState(false);
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [isModalOpening, setIsModalOpening] = useState(false);
  const { permissions, account_type } = useAuth();
  const { data: userInfo } = useBasicInfo();
  const { facilityInfo } = useGeneralProfile();

  const info = account_type === treatment_program ? facilityInfo : userInfo;

  const marketingArray = Array.isArray(marketingData)
    ? marketingData
    : [marketingData].filter(Boolean);
  const extendedArray =
    marketingArray.length > 1
      ? [...marketingArray, marketingArray[0]]
      : marketingArray;
  const currentItem = marketingArray[currentIndex % marketingArray.length];

  useEffect(() => {
    if (marketingArray.length > 1 && !isHovered) {
      const interval = setInterval(() => {
        setCurrentIndex((prevIndex) => prevIndex + 1);
      }, 8000);
      return () => clearInterval(interval);
    }
  }, [marketingArray.length, isHovered]);

  useEffect(() => {
    if (currentIndex === marketingArray.length) {
      setTimeout(() => {
        setIsTransitioning(false);
        setCurrentIndex(0);
        setTimeout(() => setIsTransitioning(true), 50);
      }, 500);
    }
  }, [currentIndex, marketingArray.length]);

  const trackMarketingEvents = (additionalData) => {
    if (permissions?.marketing_data) {
      trackClickEvent(additionalData.name, {
        page: "Homepage",
        component: "Marketing Carousal",
        ...additionalData.content,
        account_type: account_type,
        roles: info?.roles,
      });
    }
  };

  const handleSwipe = (direction) => {
    if (marketingArray.length <= 1) return;

    const nextIndex = (currentIndex + 1) % marketingArray.length;
    if (direction === "left") {
      setCurrentIndex(currentIndex + 1);
      trackMarketingEvents({
        name: "Clicked Carousel Content",
        content: {
          button: "Swipe Left",
          cardId: marketingArray[nextIndex]?.id,
          partnerName: marketingArray[nextIndex]?.heading,
          compaignType: marketingArray[nextIndex]?.campaign_type,
          paymentType: marketingArray[nextIndex]?.payment_type,
          data: new Date(
            marketingArray[nextIndex]?.created_at,
          ).toLocaleDateString("en-US", {
            month: "short",
            year: "numeric",
          }),
        },
      });
    } else if (direction === "right") {
      const prevIndex =
        currentIndex === 0 ? marketingArray.length - 1 : currentIndex - 1;
      setCurrentIndex(prevIndex);
      trackMarketingEvents({
        name: "Clicked Carousel Content",
        content: {
          button: "Swipe Right",
          cardId: marketingArray[prevIndex]?.id,
          partnerName: marketingArray[prevIndex]?.heading,
          compaignType: marketingArray[prevIndex]?.campaign_type,
          paymentType: marketingArray[prevIndex]?.payment_type,
          data: new Date(
            marketingArray[prevIndex]?.created_at,
          ).toLocaleDateString("en-US", {
            month: "short",
            year: "numeric",
          }),
        },
      });
    }
  };

  const handleImageClick = () => {
    setTimeout(() => {
      setImageModalOpen(true);
    }, 100);
    trackMarketingEvents({
      name: "Clicked Carousel Content",
      content: {
        button: "Image Click",
        cardId: currentItem.id,
        partnerName: currentItem.heading,
        compaignType: currentItem.campaign_type,
        paymentType: currentItem.payment_type,
        data: new Date(currentItem.created_at).toLocaleDateString("en-US", {
          month: "short",
          year: "numeric",
        }),
      },
    });
  };

  const handleTouchStart = (e) => {
    setStartTime(Date.now());
    setTouchStart(e.targetTouches[0].clientX);
    setTouchEnd(0);
    setHasMoved(false);
  };

  const handleTouchMove = (e) => {
    const currentX = e.targetTouches[0].clientX;
    if (Math.abs(currentX - touchStart) > 10) {
      setHasMoved(true);
    }
    setTouchEnd(currentX);
  };

  const handleTouchEnd = () => {
    const duration = Date.now() - startTime;
    const distance = touchEnd === 0 ? 0 : Math.abs(touchStart - touchEnd);

    if (!hasMoved && duration < 500 && distance < 20) {
      handleImageClick();
    } else if (touchStart && touchEnd && distance > 50) {
      const swipeDistance = touchStart - touchEnd;
      if (swipeDistance > 50) {
        handleSwipe("left");
      } else if (swipeDistance < -50) {
        handleSwipe("right");
      }
    }
  };

  const handleMouseDown = (e) => {
    setIsDragging(true);
    setStartTime(Date.now());
    setTouchStart(e.clientX);
    setTouchEnd(0);
    setHasMoved(false);
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    const currentX = e.clientX;
    if (Math.abs(currentX - touchStart) > 10) {
      setHasMoved(true);
    }
    setTouchEnd(currentX);
  };

  const handleMouseUp = () => {
    if (!isDragging) return;
    setIsDragging(false);

    const duration = Date.now() - startTime;
    const distance = touchEnd === 0 ? 0 : Math.abs(touchStart - touchEnd);

    if (!hasMoved && duration < 500 && distance < 20 && !isModalOpening) {
      setIsModalOpening(true);
      setTimeout(() => {
        setImageModalOpen(true);
        setIsModalOpening(false);
      }, 100);
      trackMarketingEvents({
        name: "Clicked Carousel Content",
        content: {
          button: "Card Click",
          cardId: currentItem.id,
          partnerName: currentItem.heading,
          compaignType: currentItem.campaign_type,
          paymentType: currentItem.payment_type,
          data: new Date(currentItem.created_at).toLocaleDateString("en-US", {
            month: "short",
            year: "numeric",
          }),
        },
      });
    } else if (touchStart && touchEnd && distance > 50) {
      const swipeDistance = touchStart - touchEnd;
      if (swipeDistance > 50) {
        handleSwipe("left");
      } else if (swipeDistance < -50) {
        handleSwipe("right");
      }
    }
  };

  return (
    <Paper
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={(e) => {
        // Only open modal if clicking outside image area and not on buttons
        if (e.target === e.currentTarget && !isModalOpening) {
          setIsModalOpening(true);
          setTimeout(() => {
            setImageModalOpen(true);
            setIsModalOpening(false);
          }, 100);
        }
      }}
      sx={{
        p: 2,
        boxShadow: "none",
        borderColor: "border.main",
        display: "flex",
        flexDirection: "column",
        gap: 2,
        cursor: "pointer",
      }}
    >
      <Box>
        <Typography
          variant="body1"
          color="text.primary"
          fontWeight={700}
          sx={{ cursor: "text" }}
        >
          What's Happening on TTN
        </Typography>
        <Box
          sx={{
            width: "100%",
            height: "1px",
            backgroundColor: "divider",
            mt: 1,
            mb: 1,
          }}
        />
        {currentItem?.heading && (
          <Typography
            variant="h6"
            color="text.primary"
            fontWeight={500}
            sx={{ cursor: "text" }}
          >
            {currentItem.heading}
          </Typography>
        )}
      </Box>

      <Box
        sx={{
          position: "relative",
          overflow: "hidden",
          borderRadius: 1,
          cursor: "grab",
          userSelect: "none",
          touchAction: "pan-y",
        }}
      >
        <Box
          sx={{
            display: "flex",
            transform: `translateX(-${currentIndex * 100}%)`,
            transition: isTransitioning ? "transform 0.5s ease-in-out" : "none",
          }}
        >
          {extendedArray.map((item, index) => {
            const mediaUrl =
              item?.image_path ||
              "https://therapy-wom-dev.s3.us-east-2.amazonaws.com/logos/5XSqPjywIt5xaaL3n5Z4aGs1XGIYMj7tEGxHVQSR.jpg";

            // Helper function to check if it's a video
            const isVideoFile = (url) => {
              if (!url) return false;
              const videoExtensions = [".mp4", ".webm", ".ogg"];
              return videoExtensions.some((ext) =>
                url.toLowerCase().endsWith(ext),
              );
            };

            const isVideo = isVideoFile(mediaUrl);

            return (
              <Box
                key={index}
                draggable={false}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleMouseDown(e);
                }}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
                sx={{
                  width: "100%",
                  height: "100%",
                  borderRadius: 1,
                  flexShrink: 0,
                  cursor: "grab",
                  userSelect: "none",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  backgroundColor: "#f5f5f5",
                  overflow: "hidden",
                  position: "relative",
                }}
              >
                {isVideo ? (
                  <Box
                    component="video"
                    src={mediaUrl}
                    autoPlay
                    loop
                    muted
                    playsInline
                    preload="metadata"
                    sx={{
                      width: "100%",
                      height: "100%",
                      maxHeight: "100%",
                      objectFit: "contain",
                      borderRadius: 1,
                      // Hide controls - they're now autoplaying
                      "&::-webkit-media-controls": {
                        display: "none !important",
                      },
                      "&::-webkit-media-controls-enclosure": {
                        display: "none !important",
                      },
                      "&::-webkit-media-controls-panel": {
                        display: "none !important",
                      },
                      "&::-webkit-media-controls-play-button": {
                        display: "none !important",
                      },
                      "&::-webkit-media-controls-start-playback-button": {
                        display: "none !important",
                      },
                    }}
                    style={{
                      // Hide controls for all browsers
                      pointerEvents: "none",
                    }}
                    // Prevent video controls from interfering with drag/swipe
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                    onTouchStart={(e) => e.stopPropagation()}
                  />
                ) : (
                  <Box
                    component="img"
                    src={mediaUrl}
                    alt="Marketing"
                    sx={{
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                      borderRadius: 1,
                    }}
                  />
                )}
              </Box>
            );
          })}
        </Box>
      </Box>

      {currentItem?.description ? (
        <Typography
          variant="h6"
          color="text.primary"
          fontSize={14}
          fontWeight={400}
        >
          {currentItem?.description}
        </Typography>
      ) : null}

      {/* {currentItem?.button_text ? ( */}
      <MarketingContent
        item={currentItem}
        showTitle={false}
        component="Marketing Carousel"
      />

      {marketingArray.length > 1 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            gap: 1,
            mt: 1,
          }}
        >
          {marketingArray.map((_, index) => (
            <Box
              key={index}
              onClick={() => {
                setCurrentIndex(index);
                trackMarketingEvents({
                  name: "Clicked Carousel Content",
                  constent: {
                    button: "Dot Click",
                    cardId: marketingArray[index]?.id,
                    partnerName: marketingArray[index]?.title,
                    compaignType: marketingArray[index]?.campaign_type,
                    paymentType: marketingArray[index]?.payment_type,
                    data: new Date(
                      marketingArray[index]?.created_at,
                    ).toLocaleDateString("en-US", {
                      month: "short",
                      year: "numeric",
                    }),
                  },
                });
              }}
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                backgroundColor:
                  index === currentIndex % marketingArray.length
                    ? "primary.main2"
                    : "#D5D5D5",
                cursor: "pointer",
                transition: "background-color 0.5s",
              }}
            />
          ))}
        </Box>
      )}

      <Modal
        open={imageModalOpen}
        onClose={() => setImageModalOpen(false)}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "auto", // Enable scrolling for the modal backdrop
        }}
      >
        <Box
          sx={{
            position: "relative",
            width: "400px",
            maxHeight: "90vh", // Limit height to 90% of viewport
            backgroundColor: "white",
            borderRadius: 2,
            p: 2,
            outline: "none",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
            overflow: "auto", // Enable scrolling for modal content
            // Optional: Add smooth scrolling
            "&::-webkit-scrollbar": {
              width: "8px",
            },
            "&::-webkit-scrollbar-track": {
              background: "#f1f1f1",
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "#c1c1c1",
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              background: "#a8a8a8",
            },
          }}
        >
          <IconButton
            onClick={() => setImageModalOpen(false)}
            sx={{
              position: "sticky", // Changed to sticky so it stays visible while scrolling
              top: 8,
              right: 8,
              alignSelf: "flex-end",
              backgroundColor: "rgba(0,0,0,0.5)",
              color: "white",
              zIndex: 1,
              "&:hover": {
                backgroundColor: "rgba(0,0,0,0.7)",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
          {/* <Box
            component="img"
            src={
              currentItem?.image_path ||
              "https://therapy-wom-dev.s3.us-east-2.amazonaws.com/logos/5XSqPjywIt5xaaL3n5Z4aGs1XGIYMj7tEGxHVQSR.jpg"
            }
            alt="Marketing Enlarged"
            sx={{
              width: "100%",
              maxWidth: "300px",
              height: "auto",
              objectFit: "contain",
              borderRadius: 1,
              flexShrink: 0, // Prevent image from shrinking
            }}
          /> */}
          <Box
            sx={{
              width: "100%",
              maxWidth: "300px",
              height: "auto",
              borderRadius: 1,
              flexShrink: 0,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            {isVideoFile(currentItem?.image_path) ? (
              <Box
                component="video"
                src={
                  currentItem?.image_path ||
                  "https://therapy-wom-dev.s3.us-east-2.amazonaws.com/logos/5XSqPjywIt5xaaL3n5Z4aGs1XGIYMj7tEGxHVQSR.jpg"
                }
                autoPlay
                loop
                muted
                playsInline
                preload="metadata"
                controls
                sx={{
                  width: "100%",
                  maxWidth: "300px",
                  height: "auto",
                  maxHeight: "300px",
                  objectFit: "contain",
                  borderRadius: 1,
                }}
              />
            ) : (
              <Box
                component="img"
                src={currentItem?.image_path}
                alt="Marketing Enlarged"
                sx={{
                  width: "100%",
                  maxWidth: "300px",
                  height: "auto",
                  objectFit: "contain",
                  borderRadius: 1,
                }}
              />
            )}
          </Box>
          <MarketingContent
            item={currentItem}
            showTitle={false}
            component="Marketing Modal"
            sx={{
              width: "100%", // Ensure content takes full width
              flexShrink: 0, // Prevent content from shrinking
            }}
          />
        </Box>
      </Modal>
    </Paper>
  );
};

export default MarketingCard;
